import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";
import Seo from "@/components/Seo";
import { quizQuestions, scoreQuiz } from "@/data/quiz";
import { getCountry } from "@/data/countries";

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const finished = step >= quizQuestions.length;

  const results = useMemo(() => (finished ? scoreQuiz(answers).slice(0, 3) : []), [finished, answers]);

  const choose = (opt) => {
    setAnswers((a) => [...a, opt]);
    setStep((s) => s + 1);
  };

  const back = () => {
    setAnswers((a) => a.slice(0, -1));
    setStep((s) => Math.max(0, s - 1));
  };

  const reset = () => {
    setAnswers([]);
    setStep(0);
  };

  const q = quizQuestions[step];

  return (
    <div data-testid="quiz-page" className="pt-24 lg:pt-28 bg-pine-ink min-h-screen grain relative">
      <Seo
        title="Find Your Hijrah Destination — The Decision Tree | Hijrah Mom"
        siteName="Hijrah Mom"
        description="Answer seven honest questions about budget, climate, language, visa path, family, and Arabic & Qur'an priorities — and get your top three hijrah destination countries."
      />

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-14 lg:py-20">
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-petal-soft">The decision tree</p>
          <h1 className="mt-4 font-serif font-bold text-cream text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            Where is Allah <span className="italic text-petal-soft">calling you?</span>
          </h1>
          <p className="mt-4 text-sage-light">Seven questions. No email. No judgment. Just direction.</p>
        </div>

        {/* progress */}
        <div className="flex items-center justify-center gap-2 mb-10" data-testid="quiz-progress">
          {quizQuestions.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i < step ? "w-8 bg-petal" : i === step && !finished ? "w-8 bg-petal-soft" : "w-4 bg-cream/15"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {!finished ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="bg-pine-dark border border-cream/10 rounded-3xl p-7 lg:p-10 shadow-2xl"
              data-testid="quiz-card"
            >
              <p className="font-mono text-xs tracking-[0.3em] text-petal-soft">QUESTION {q.step} / {String(quizQuestions.length).padStart(2, "0")}</p>
              <h2 className="mt-4 font-serif font-semibold text-cream text-2xl lg:text-3xl leading-snug">{q.question}</h2>
              <p className="mt-2 text-sm text-sage-light italic">{q.hint}</p>
              <div className="mt-8 space-y-3">
                {q.options.map((opt) => (
                  <motion.button
                    key={opt.label}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`quiz-option-${q.id}-${opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                    onClick={() => choose(opt)}
                    className="w-full text-left bg-pine-ink border border-cream/15 rounded-xl px-5 py-4 hover:border-petal hover:bg-pine transition-colors duration-300 group"
                  >
                    <span className="flex items-center justify-between gap-4">
                      <span>
                        <span className="block text-cream font-semibold text-sm sm:text-base">{opt.label}</span>
                        <span className="block text-xs text-sage-light mt-0.5">{opt.desc}</span>
                      </span>
                      <ArrowRight size={17} className="text-cream/30 group-hover:text-petal-soft group-hover:translate-x-1 transition-all shrink-0" />
                    </span>
                  </motion.button>
                ))}
              </div>
              {step > 0 && (
                <button
                  data-testid="quiz-back-btn"
                  onClick={back}
                  className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-sage-light hover:text-petal-soft transition-colors"
                >
                  <ArrowLeft size={13} /> Back
                </button>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="quiz-results"
            >
              <div className="text-center mb-10">
                <p className="font-serif italic text-2xl text-petal-soft">MashaAllah — your tree has bloomed.</p>
                <p className="mt-2 text-sm text-sage-light">Your top three destinations, matched from your seven answers:</p>
              </div>
              <div className="space-y-4">
                {results.map((r, i) => {
                  const c = getCountry(r.slug);
                  if (!c) return null;
                  return (
                    <motion.div
                      key={r.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.15, duration: 0.5 }}
                    >
                      <Link
                        to={`/countries/${c.slug}`}
                        data-testid={`quiz-result-${r.slug}`}
                        className="flex items-center gap-5 bg-pine-dark border border-cream/10 rounded-2xl p-5 hover:border-petal transition-colors duration-300 group"
                      >
                        <span className={`font-serif font-bold text-3xl w-10 shrink-0 ${i === 0 ? "text-petal-soft" : "text-outline"}`}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <img src={c.image} alt={c.imageAlt} className="w-16 h-16 rounded-xl object-cover shrink-0" loading="lazy" />
                        <span className="flex-1 min-w-0">
                          <span className="block font-serif font-semibold text-cream text-xl">{c.name}</span>
                          <span className="block text-xs text-sage-light truncate italic">{c.tagline}</span>
                        </span>
                        <span className="text-right shrink-0">
                          <span className="block font-mono text-2xl text-petal-soft" data-testid={`quiz-match-${r.slug}`}>{r.match}%</span>
                          <span className="block font-mono text-[10px] tracking-widest uppercase text-sage-light">match</span>
                        </span>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <button
                  data-testid="quiz-restart-btn"
                  onClick={reset}
                  className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-6 py-3 rounded-full hover:bg-cream/10 transition-colors"
                >
                  <RotateCcw size={15} /> Retake
                </button>
                <Link
                  to="/steps"
                  data-testid="quiz-next-steps-btn"
                  className="inline-flex items-center gap-2 bg-petal text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-cream hover:text-pine transition-colors"
                >
                  Now plan the move <ArrowRight size={15} />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
